import React, { useContext, useEffect, useState } from 'react';
import { PlusIcon, EllipsisVerticalIcon } from '@heroicons/react/16/solid';
import { Dropdown, DropdownButton, DropdownItem, DropdownMenu } from '../../../components/Dropdown';
import { Badge } from '../../../components/Badge';
import { Link } from '../../../components/Link';
import { Button } from '../../../components/Button';
import { Heading } from '../../../components/Heading';
import { Avatar } from '../../../components/Avatar';
import Loading from '../../../components/Loading';
import Pagination from '../../../components/Pagination'; // Import the Pagination component
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle } from '../../../components/Dialog';
import { SetupUsergroup } from '../../../models/SetupUsergroup';
import { useSetupUsergroup } from '../../../hooks/useSetupUsergroup';
import { UserContext } from '../../../context/userContext';
import { Text } from '../../../components/Text';
import { Card } from '../../../components/Card';
import { ListToolbarRow, ListToolbarSearch, FiltersButton, SortMenu } from '../../../components/ListToolbar';
import { FiltersDialog, FilterPills, FilterPill } from '../../../components/FiltersDialog';
import { DateRangePicker } from '../../../components/DateRangePicker';
import { Select } from '../../../components/Select';
import { useWarehouses } from '../../../hooks/useWarehouse';

const SORT_OPTIONS = [
    { label: 'Name (A-Z)', value: 'az' },
    { label: 'Name (Z-A)', value: 'za' },
];

const SetupCustomersPage: React.FC = () => {
    const { getCustomers, getCustomersByWarehouseId, loading, error, deleteUserGroup, fetchCustomer, totalCustomers } = useSetupUsergroup();
    const [customers, setCustomers] = useState<SetupUsergroup[]>([]);
    const [searchQuery, setSearchQuery] = useState<string>(''); // State to store the search query
    const [currentPage, setCurrentPage] = useState<number>(1); // State to track the current page
    const [usersPerPage] = useState<number>(10); // Number of users per page
    const [isOpen, setIsOpen] = useState(false); // Dialog open state
    const [isDeleting, setIsDeleting] = useState(false); // Loading state for deletion
    const [selectedUsergroupId, setSelectedUsergroupId] = useState<string | null>(null); // ID of the user to be deleted
    const navigate = useNavigate();
    const { currentUser } = useContext(UserContext);
    const { warehouses } = useWarehouses();
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [sortOrder, setSortOrder] = useState('az');
    const [filterStartDate, setFilterStartDate] = useState('');
    const [filterEndDate, setFilterEndDate] = useState('');
    const [filterYear, setFilterYear] = useState('');
    const [filterWarehouseId, setFilterWarehouseId] = useState('');

    const resetFilters = () => {
        setFilterStartDate('');
        setFilterEndDate('');
        setFilterYear('');
        setFilterWarehouseId('');
    };

    const availableYears = Array.from(
        new Set(
            customers
                .map((c) => new Date(c.created_time).getFullYear())
                .filter((year) => !Number.isNaN(year))
        )
    ).sort((a, b) => b - a);

    const activeFilterCount = [filterStartDate, filterEndDate, filterYear, filterWarehouseId].filter(Boolean).length;

    const filterPills: FilterPill[] = [
        ...(filterStartDate ? [{ key: 'from', label: `From: ${filterStartDate}`, onClear: () => setFilterStartDate('') }] : []),
        ...(filterEndDate ? [{ key: 'to', label: `To: ${filterEndDate}`, onClear: () => setFilterEndDate('') }] : []),
        ...(filterYear ? [{ key: 'year', label: `Year: ${filterYear}`, onClear: () => setFilterYear('') }] : []),
        ...(filterWarehouseId ? [{
            key: 'warehouse',
            label: `Warehouse: ${warehouses?.find((w) => w.id?.toString() === filterWarehouseId)?.warehouse_name || filterWarehouseId}`,
            onClear: () => setFilterWarehouseId(''),
        }] : []),
    ];

    // Filter users based on the search query
    const filteredUsergroups = customers.filter((usergroups) => {
        const matchesSearch = usergroups.fullname.toLowerCase().includes(searchQuery.toLowerCase()) ||
            usergroups.email?.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesStartDate = !filterStartDate || new Date(usergroups.created_time) >= new Date(filterStartDate);
        const matchesEndDate = !filterEndDate || new Date(usergroups.created_time) <= new Date(filterEndDate);
        const matchesYear = !filterYear || new Date(usergroups.created_time).getFullYear().toString() === filterYear;
        const matchesWarehouse = !filterWarehouseId || usergroups.warehouse_id === Number(filterWarehouseId);

        return matchesSearch && matchesStartDate && matchesEndDate && matchesYear && matchesWarehouse;
    }, []);

    // Pagination logic
    const indexOfLastUser = currentPage * usersPerPage;
    const indexOfFirstUser = indexOfLastUser - usersPerPage;
    const currentUsergroups = filteredUsergroups.slice(indexOfFirstUser, indexOfLastUser);
    const totalPages = Math.ceil(filteredUsergroups.length / usersPerPage);

    const fetchCustomerList = async () => {
        try {
            // Check if user has HQ access
            if (currentUser?.warehouse_hq === "1") {
                const fetchedCustomer = await getCustomers();
                setCustomers(fetchedCustomer || []);
            } else if (currentUser?.warehouse_id) {
                const fetchedCustomer = await getCustomersByWarehouseId(currentUser.warehouse_id.toString());
                setCustomers(fetchedCustomer || []);
            }
        } catch (error) {
            console.error('Failed to fetch customers:', error);
        }
    };

    useEffect(() => {
        fetchCustomerList();
    }, [currentUser]); // Dependencies to re-run on change

    // Open the delete dialog with the selected user's ID
    const handleOpenDeleteDialog = (customerId: string) => {
        setSelectedUsergroupId(customerId);
        setIsOpen(true);
    };

    // Handle delete customer
    const handleDelete = async () => {
        if (!selectedUsergroupId) return;

        setIsDeleting(true);
        try {
            await deleteUserGroup(Number(selectedUsergroupId));
            fetchCustomerList(); // Refresh the usergroup list
            setIsOpen(false);
        } catch (error) {
            console.error('Failed to delete customer:', error);
        } finally {
            setIsDeleting(false);
        }
    };

    useEffect(() => {
        document.title = "Customers";
    }, []);

    return (
        <>
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div className="max-sm:w-full sm:flex-1">
                    <Heading>Customers</Heading>
                </div>
                <Button onClick={() => navigate('/gp/setup/customers/add')}><PlusIcon />Create new customer</Button>
            </div>

            <Card className="mt-6 p-5">
            <ListToolbarRow search={<ListToolbarSearch value={searchQuery} onChange={setSearchQuery} placeholder="Search customer…" />}>
                <FiltersButton count={activeFilterCount} onClick={() => setIsFilterOpen(true)} />
                <SortMenu options={SORT_OPTIONS} value={sortOrder} onChange={setSortOrder} />
            </ListToolbarRow>
            <FilterPills pills={filterPills} onClearAll={resetFilters} />

            <ul>
                {loading && <div className='w-full text-center h-full py-36 justify-center'><Loading /></div>}
                {error && <p className='py-16 text-center text-red-700'>{error}</p>}
                {!loading && !error && currentUsergroups.length === 0 && <p className='py-16 text-center'>No customers found.</p>}
                {!loading && !error && [...currentUsergroups].sort((a, b) => sortOrder === 'az' ? a.fullname.localeCompare(b.fullname) : b.fullname.localeCompare(a.fullname)).map((usergroup) => (
                    <li key={usergroup.id} className="rounded-xl hover:bg-secondary">
                        <div className="flex items-center justify-between px-2">
                            <div className="flex gap-6 py-5">
                                <Avatar square initials={usergroup.fullname[0]} className="bg-brand-teal/10 text-brand-teal size-12" />
                                <div className="">
                                    <div className="text-base/6 font-semibold">
                                        <Link href={`/gp/setup/customers/view/${usergroup.id}`}>{usergroup.fullname}</Link>
                                    </div>
                                    <div className="text-xs/6 text-zinc-600">
                                        {usergroup.email ? usergroup.email : 'Undefined email'}
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-4">
                                <Badge className="max-sm:hidden" color='lime'>
                                    {usergroup.warehouse_name! || '-'}
                                </Badge>
                                <Dropdown>
                                    <DropdownButton plain aria-label="More options">
                                        <EllipsisVerticalIcon />
                                    </DropdownButton>
                                    <DropdownMenu anchor="bottom end">
                                        <DropdownItem onClick={() => navigate(`/gp/setup/customers/view/${usergroup.id}`)}>View</DropdownItem>
                                        <DropdownItem onClick={() => navigate(`/gp/setup/customers/edit/${usergroup.id}`)}>Edit</DropdownItem>
                                        <DropdownItem onClick={() => handleOpenDeleteDialog(usergroup.id!.toString())}>Delete</DropdownItem>
                                    </DropdownMenu>
                                </Dropdown>
                            </div>
                        </div>
                    </li>
                ))}
            </ul>

            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                totalResult={customers.length}
                dataName='Customers'
            />
            </Card>

            <FiltersDialog open={isFilterOpen} onClose={() => setIsFilterOpen(false)} onReset={resetFilters}>
                <div className="sm:col-span-2">
                    <Text>Date Range</Text>
                    <DateRangePicker
                        from={filterStartDate}
                        to={filterEndDate}
                        onChange={({ from, to }) => { setFilterStartDate(from); setFilterEndDate(to); }}
                        className="w-full"
                    />
                </div>
                <div>
                    <Text>By Year</Text>
                    <Select value={filterYear} onChange={(e) => setFilterYear(e.target.value)}>
                        <option value="">All Years</option>
                        {availableYears.map((year) => (
                            <option key={year} value={year.toString()}>{year}</option>
                        ))}
                    </Select>
                </div>
                {(currentUser?.role.toString() === "1") && (
                    <div>
                        <Text>By Warehouse</Text>
                        <Select value={filterWarehouseId} onChange={(e) => setFilterWarehouseId(e.target.value)}>
                            <option value="">All Warehouses</option>
                            {warehouses?.map((warehouse) => (
                                <option key={warehouse.id} value={warehouse.id?.toString()}>
                                    {warehouse.warehouse_name}
                                </option>
                            ))}
                        </Select>
                    </div>
                )}
            </FiltersDialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={isOpen} onClose={() => setIsOpen(false)}>
                <DialogTitle>Delete customer</DialogTitle>
                <DialogDescription>
                    Are you sure you want to delete this customer? This action cannot be undone.
                </DialogDescription>
                <DialogActions>
                    <Button plain onClick={() => setIsOpen(false)} disabled={isDeleting}>
                        Cancel
                    </Button>
                    <Button onClick={handleDelete} disabled={isDeleting}>
                        {isDeleting ? <Loading /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default SetupCustomersPage;
